import React, { useState } from "react"
import clsx from "clsx"
import {
	ThemeProvider,
	Dialog,
	IconButton,
	Typography,
	Grid,
	Button,
	CircularProgress
} from "@material-ui/core"
import {
	Close as CloseIcon,
	Refresh as RefreshIcon,
	FileCopyOutlined as CopyIcon,
	Check as CheckIcon
} from "@material-ui/icons"

import theme from "@/styles/theme"

import { SparkleSingleIcon } from "@/assets/icons"

import { SvgIcon, Notification, Loading } from "@/components"

import copy from "copy-to-clipboard"

import { renderComponent, unmountComponent } from "@/utils/node"

import { playgroundIAUrl } from "@/config/url"

import AttendanceProtocolSummaryService from "@/services/AttendanceProtocolSummary"
import ClientNoteService from "@/services/ClientNote"
import { ErrorType } from "@/hooks/useValidation"
import ErrorHandlerService from "@/services/ErrorHandler"

import useDidMount from "@/hooks/useDidMount"
import usePageEvent from "@/hooks/usePageEvent"

import AttendanceSummaryModalSkeleton from "@/skeletons/AttendanceSummaryModal"

import useStyles from "@/components/Skynet/AttendanceSummary/NotificationMessage/AttendanceSummaryFooter/AttendanceSummaryModal/styles"

import { colors } from "@/styles/colors"

import {
	AttendanceSummary
} from "@/protocols/attendanceSummary"
import { UserInInstanceRoleName } from "@/protocols/userInInstance"

export type AttendanceSummaryModalProps = {
	inboxChannelChatAttendanceId: number
	contactName: string
	userRoleName: UserInInstanceRoleName
	clientId: number
	inboxChannelChatId: number
	onClose?: () => void
}

type AttendanceSummaryModalType = {
	open: (props: AttendanceSummaryModalProps) => void
	close: () => void
}

const AttendanceSummaryModal: AttendanceSummaryModalType & React.FC<AttendanceSummaryModalProps> = (props) => {
	const {
		inboxChannelChatAttendanceId,
		contactName,
		userRoleName,
		clientId,
		inboxChannelChatId,
		onClose
	} = props

	const [isOpen, setIsOpen] = useState(true)
	const [isLoading, setIsLoading] = useState(true)
	const [summaryText, setSummaryText] = useState<string>("")
	const [summary, setSummary] = useState<AttendanceSummary | null>(null)
	const [hasCopied, setHasCopied] = useState(false)
	const [isSavingNote, setIsSavingNote] = useState(false)
	const pageEvent = usePageEvent()

	const classes = useStyles()

	const COPY_FEEDBACK_REVERT_DELAY_IN_MS = 2000

	const handleLoadSummary = async (forceRegenerate: boolean) => {
		setIsLoading(true)
		setSummaryText("")

		try {
			if (!forceRegenerate) {
				const cachedSummary = await AttendanceProtocolSummaryService.getSummary(inboxChannelChatAttendanceId)
				if (cachedSummary) {
					setSummaryText(cachedSummary.content)
					setSummary(cachedSummary)
					setIsLoading(false)
					return
				}
			}

			await AttendanceProtocolSummaryService.streamSummary(inboxChannelChatAttendanceId, {
				forceRegenerate,
				onTextChunk: (textChunk) => {
					setSummaryText(text => text + textChunk)
				}
			})

			const summaryMetadata = await AttendanceProtocolSummaryService.getSummary(inboxChannelChatAttendanceId)
			if (summaryMetadata) {
				setSummary(summaryMetadata)
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setIsLoading(false)
	}

	const handleClose = () => {
		setIsOpen(false)
		onClose?.()
	}

	const handleRegenerate = () => {
		handleLoadSummary(true)
	}

	const handleCopySummary = () => {
		if (!summaryText) {
			return
		}

		copy(summaryText.trim())
		setHasCopied(true)
		Notification.success({
			message: "Resumo copiado com sucesso!"
		})

		window.setTimeout(() => {
			setHasCopied(false)
		}, COPY_FEEDBACK_REVERT_DELAY_IN_MS)
	}

	const handleSaveSummaryAsNote = async () => {
		if (!summaryText) {
			return
		}

		setIsSavingNote(true)

		try {
			await ClientNoteService.create({
				clientId,
				inboxChannelChatId,
				content: summaryText
			})
			/**
			 * Notifies 'ClientNotesTable' component to reload its notes list,
			 * since the summary was just saved as a new note from this modal.
			 */
			pageEvent.emit("ClientNoteCreated")
			Notification.success({
				message: "Nota salva no chat!"
			})
			handleClose()
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setIsSavingNote(false)
	}

	const handleMoreInsights = () => {
		const COMMERCIAL_INSIGHTS_INITIAL_PROMPT = `Quais oportunidades comerciais (upsell, retenção, objeções) o atendimento '${inboxChannelChatAttendanceId}' do contato '${contactName}' revela?`

		const searchParams = new URLSearchParams({
			initialPrompt: COMMERCIAL_INSIGHTS_INITIAL_PROMPT,
			inboxChannelChatId: String(inboxChannelChatId)
		})

		const playgroundUrl = `${playgroundIAUrl.cpURL}?${searchParams.toString()}`

		window.open(playgroundUrl, "_blank")
		handleClose()
	}

	useDidMount(() => {
		handleLoadSummary(false)
	})

	const isManagerVariant = userRoleName === "Gestor"
	const canRegenerate = Boolean(summary?.canRegenerate)

	return (
		<ThemeProvider theme={theme}>
			<Dialog
				open={isOpen}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				classes={{ paper: classes.dialogPaper }}
			>
				<Grid container>
					<Grid item xs={12}>
						<Grid
							container
							className={classes.header}
							alignItems="center"
							justifyContent="space-between"
						>
							<Grid item>
								<Grid
									container
									alignItems="center"
									spacing={1}
								>
									<Grid item>
										<SvgIcon
											icon={SparkleSingleIcon}
											htmlColor={colors.gray[0]}
											autoSize
											className={classes.sparklesIcon}
										/>
									</Grid>
									<Grid item>
										<Typography className={classes.headerTitle}>
											Resumo com IA
										</Typography>
										<Typography className={classes.headerSubtitle}>
											Protocolo {inboxChannelChatAttendanceId} · {contactName}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item>
								<IconButton
									className={classes.closeIconButton}
									onClick={handleClose}
									size="small"
									disableFocusRipple
								>
									<CloseIcon />
								</IconButton>
							</Grid>
						</Grid>
					</Grid>

					<Grid item xs={12}>
						<Grid className={classes.body}>
							{!isLoading && summaryText && (
								<IconButton
									className={classes.copyButton}
									onClick={handleCopySummary}
									size="small"
									disableFocusRipple
								>
									{hasCopied ? <CheckIcon className={classes.copyIcon} /> : <CopyIcon className={classes.copyIcon} />}
								</IconButton>
							)}

							<Loading
								loading={isLoading && !summaryText}
								customLoadingElement={<AttendanceSummaryModalSkeleton />}
							>
								<Typography className={classes.summaryText}>
									{summaryText || "Não foi possível gerar o resumo."}
								</Typography>
							</Loading>
						</Grid>
					</Grid>

					<Grid item xs={12}>
						<Grid
							container
							className={classes.footer}
							justifyContent="flex-end"
							alignItems="center"
							spacing={1}
						>
							<Grid item>
								<Button
									className={`${classes.footerButton} ${classes.saveNoteButton}`}
									variant="outlined"
									onClick={handleSaveSummaryAsNote}
									disabled={isLoading || isSavingNote}
									endIcon={isSavingNote ? <CircularProgress size={16} color="inherit" /> : null}
									disableFocusRipple
								>
								Salvar nota
								</Button>
							</Grid>
							{canRegenerate && (
								<Grid item>
									<Button
										className={`${classes.footerButton} ${classes.regenerateButton}`}
										variant="text"
										onClick={handleRegenerate}
										disabled={isLoading}
										startIcon={<RefreshIcon />}
										disableFocusRipple
									>
										Gerar novamente
									</Button>
								</Grid>
							)}
							{isManagerVariant && (
								<Grid item>
									<Button
										className={clsx(classes.footerButton, classes.moreInsightsButton)}
										variant="contained"
										onClick={handleMoreInsights}
										disabled={isLoading}
										disableFocusRipple
									>
										Mais insights →
									</Button>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Dialog>
		</ThemeProvider>
	)
}

const attendanceSummaryModalComponentId = "attendance-summary-modal"

AttendanceSummaryModal.open = (props: AttendanceSummaryModalProps) => {
	renderComponent(
		attendanceSummaryModalComponentId,
		<AttendanceSummaryModal
			{...props}
		/>
	)
}

AttendanceSummaryModal.close = () => {
	unmountComponent(attendanceSummaryModalComponentId)
}

export default AttendanceSummaryModal
